/**
 * Which trend series are expanded, shared by every pane that plots one
 * (REQ-STATUS-074, REQ-STATUS-079).
 *
 * Extracted when the connection drawer gained the affordance: the component
 * pane already owned this state, and a second copy in the drawer would have
 * drifted the first time either changed (CLAUDE.md §4.4).
 *
 * ⚠ **Session-local by construction.** `useState`, deliberately not the Profile
 * working state the window selector writes to — expanding a plot is a momentary
 * investigation, and a Profile-captured set would re-open charts about a problem
 * resolved days ago. Allowing SEVERAL strengthens that: N stale charts is worse
 * than one.
 *
 * ⚠ **A set, not a single key.** The original one-at-a-time exclusion was
 * retired 2026-08-03 — it made comparing two metrics a matter of toggling back
 * and forth while holding one of them in your head, which is the analysis the
 * feature exists to support.
 */
import { useState } from "react";

export interface TrendExpansion {
  /** Whether one series is currently expanded. */
  isExpanded: (key: string) => boolean;
  /** Expand a collapsed series, or collapse an expanded one. */
  toggle: (key: string) => void;
  /** How many are expanded — the bulk control appears only above one. */
  count: number;
  /** Collapse every expanded series at once. */
  collapseAll: () => void;
}

export function useTrendExpansion(): TrendExpansion {
  const [keys, setKeys] = useState<ReadonlySet<string>>(() => new Set());
  return {
    isExpanded: (key) => keys.has(key),
    toggle: (key) =>
      setKeys((current) => {
        const next = new Set(current);
        // `delete` reports whether it removed anything, which makes the toggle
        // one branch rather than a has-then-add-or-remove pair.
        if (!next.delete(key)) next.add(key);
        return next;
      }),
    count: keys.size,
    collapseAll: () => setKeys(new Set()),
  };
}
